import { Box, Card, Dialog, DialogContentText, Slide, Stack, useMediaQuery } from '@mui/material'
import styled from 'styled-components'
import { TransitionProps } from '@mui/material/transitions'
import React from 'react'
import theme from '../../../theme/theme'
import Button from '../Button'
import clash from '../../../assets/clash.svg'
import question from '../../../assets/question.svg'
import HeadingTwo from '../HeadingTwo'
import { isMobileDown } from '../../../theme/deviceChecks'

export type ModalProps = {
  title: string
  message: string
  dropdown?: React.ReactElement
  banner?: React.ReactNode
  type: 'clash' | 'question'
  buttonLabel: string
  onClick?: () => void
  open: boolean
  onClose: () => void
  isDisabled?: boolean
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | undefined
  buttonLoading?: boolean
  confirmButtonStyle?: React.CSSProperties
  cancelButtonStyle?: React.CSSProperties
  subMessage?: string
}

const StyledStack = styled(Stack)`
  ${theme.breakpoints.down('sm')} {
    flex-direction: column !important;
  }
`

const StyledDialogContentText = styled(DialogContentText)`
  padding-top: 20px;
  text-align: center;
  white-space: pre-wrap;
`

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
)

function Modal({
  title,
  message,
  dropdown,
  banner,
  type,
  buttonLabel,
  onClick,
  open,
  onClose,
  isDisabled,
  maxWidth,
  buttonLoading = false,
  confirmButtonStyle,
  cancelButtonStyle,
  subMessage = ''
}: ModalProps) {
  const mobile = useMediaQuery(isMobileDown)

  let image
  if (type === 'clash') {
    image = (
      <img
        src={`${clash}`}
        alt={title}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
      />
    )
  } else if (type === 'question') {
    image = (
      <img
        src={`${question}`}
        alt={title}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
      />
    )
  }

  return (
    <Card data-testid="modal">
      <Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth={maxWidth}>
        <StyledStack direction="row" sx={{ minHeight: '500px' }}>
          {!mobile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                width: '320px',
                minHeight: '100%',
                flexShrink: 0
              }}
            >
              {image}
            </Box>
          )}
          <StyledStack
            direction="column"
            alignItems="center"
            justifyContent="space-evenly"
            pt={2}
            pb={2}
            pr={4}
            pl={4}
            sx={{
              minWidth: 0,
              maxWidth: mobile ? '100%' : '600px',
            }}
          >
            <HeadingTwo title={title} />
            <StyledDialogContentText id="alert-dialog-description">
              {message}
            </StyledDialogContentText>
            {banner && (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  '& *': {
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  },
                }}
              >
                {banner}
              </Box>
            )}
            {dropdown}
            <StyledDialogContentText id="alert-dialog-description">
              {subMessage}
            </StyledDialogContentText>
            <Button
              label={buttonLabel}
              variant="outlined"
              color="primary"
              onClick={onClick}
              disabled={isDisabled}
              loading={buttonLoading}
              style={confirmButtonStyle}
              dataTestId="modal-confirm-button"
            />
            <Button
              label="Close"
              variant="text"
              color="primary"
              onClick={onClose}
              style={{
                marginTop: mobile ? '20px' : '0px',
                ...cancelButtonStyle,
              }}
              dataTestId="modal-cancel-button"
            />
          </StyledStack>
        </StyledStack>
      </Dialog>
    </Card>
  )
}

export default Modal
